/**
 * Enhanced Ingredient Philosophy Section Interactions
 * Adds premium animations and micro-interactions for the ingredient cards
 */

class IngredientPhilosophyEnhancer {
  constructor() {
    this.section = document.querySelector('.divayur-ingredient-philosophy');
    this.cards = document.querySelectorAll('.divayur-ingredient-philosophy__card');
    this.observer = null;
    this.isVisible = false;
    
    this.init();
  }

  init() {
    if (!this.section) return;
    
    this.setupIntersectionObserver();
    this.bindEvents();
    this.enhanceAccessibility();
  }

  setupIntersectionObserver() {
    const options = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px'
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isVisible) {
          this.isVisible = true;
          this.animateCardsIn();
        }
      });
    }, options);

    this.observer.observe(this.section);
  }

  animateCardsIn() {
    this.cards.forEach((card, index) => {
      setTimeout(() => {
        card.style.animationDelay = `${index * 0.1}s`;
        card.classList.add('animate-in');
      }, index * 100);
    });
  }

  bindEvents() {
    this.cards.forEach(card => {
      // Enhanced hover effects
      card.addEventListener('mouseenter', this.handleCardHover.bind(this));
      card.addEventListener('mouseleave', this.handleCardLeave.bind(this));
      
      // Touch support for mobile
      card.addEventListener('touchstart', this.handleCardTouch.bind(this));
      
      // Focus events for accessibility
      const link = card.querySelector('.divayur-ingredient-philosophy__link');
      if (link) {
        link.addEventListener('focus', () => this.handleCardFocus(card));
        link.addEventListener('blur', () => this.handleCardBlur(card));
      }
    });

    // Parallax effect on scroll (subtle)
    if (window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      window.addEventListener('scroll', this.handleScroll.bind(this));
    }
  }

  handleCardHover(e) {
    const card = e.currentTarget;
    const image = card.querySelector('.divayur-ingredient-philosophy__img');
    
    if (image) {
      // Add subtle rotation to image
      image.style.transform = 'scale(1.1) rotate(3deg)';
    }

    // Add glow effect
    card.style.setProperty('--glow-opacity', '1');
    
    // Lift adjacent cards slightly
    this.liftAdjacentCards(card, true);
  }

  handleCardLeave(e) {
    const card = e.currentTarget;
    const image = card.querySelector('.divayur-ingredient-philosophy__img');
    
    if (image) {
      image.style.transform = '';
    }

    card.style.setProperty('--glow-opacity', '0');
    
    // Reset adjacent cards
    this.liftAdjacentCards(card, false);
  }

  handleCardTouch(e) {
    // Add haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
  }

  handleCardFocus(card) {
    card.classList.add('focused');
  }

  handleCardBlur(card) {
    card.classList.remove('focused');
  }

  liftAdjacentCards(activeCard, lift) {
    const cardIndex = Array.from(this.cards).indexOf(activeCard);
    const transform = lift ? 'translateY(-4px)' : '';
    
    // Previous card
    if (cardIndex > 0) {
      this.cards[cardIndex - 1].style.transform = transform;
    }
    
    // Next card
    if (cardIndex < this.cards.length - 1) {
      this.cards[cardIndex + 1].style.transform = transform;
    }
  }

  handleScroll() {
    if (!this.isVisible) return;

    const scrolled = window.pageYOffset;
    const sectionTop = this.section.offsetTop;
    const sectionHeight = this.section.offsetHeight;
    const windowHeight = window.innerHeight;

    // Only apply parallax when section is in view
    if (scrolled + windowHeight > sectionTop && scrolled < sectionTop + sectionHeight) {
      const progress = (scrolled + windowHeight - sectionTop) / (sectionHeight + windowHeight);
      const parallaxOffset = (progress - 0.5) * 30; // Subtle parallax
      
      this.section.style.transform = `translateY(${parallaxOffset}px)`;
    }
  }

  enhanceAccessibility() {
    // Add ARIA labels and roles
    this.cards.forEach((card, index) => {
      card.setAttribute('role', 'article');
      card.setAttribute('aria-label', `Ingredient ${index + 1}`);
      
      const link = card.querySelector('.divayur-ingredient-philosophy__link');
      if (link && link instanceof HTMLElement) {
        link.setAttribute('aria-describedby', `ingredient-desc-${index}`);
        // Ensure button text is visible
        link.style.setProperty('color', '#000000', 'important');
        link.style.setProperty('text-decoration', 'none', 'important');
      }
      
      const description = card.querySelector('.divayur-ingredient-philosophy__description');
      if (description) {
        description.setAttribute('id', `ingredient-desc-${index}`);
      }
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    window.removeEventListener('scroll', this.handleScroll);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new IngredientPhilosophyEnhancer();
});

// CSS Custom Properties for dynamic effects
const style = document.createElement('style');
style.textContent = `
  .divayur-ingredient-philosophy__card {
    --glow-opacity: 0;
  }
  
  .divayur-ingredient-philosophy__card::after {
    content: '';
    position: absolute;
    inset: -4px;
    background: conic-gradient(from 0deg, transparent, var(--accent-color), transparent);
    border-radius: inherit;
    opacity: var(--glow-opacity);
    z-index: -1;
    transition: opacity 0.3s ease;
    animation: rotate 4s linear infinite;
  }
  
  .divayur-ingredient-philosophy__card.focused {
    outline: 3px solid var(--accent-color);
    outline-offset: 4px;
  }
  
  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  @media (prefers-reduced-motion: reduce) {
    .divayur-ingredient-philosophy__card::after {
      animation: none;
    }
    
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
`;

document.head.appendChild(style);
